import { isEqual, cloneDeep } from 'lodash';

interface ITimePeriod {
  quantity: number;
  unit: string;
}

export const getType = (param: any) => {
  let res = Object.prototype.toString.call(param);
  res = res.split(' ')[1];
  res = res.replace(']', '');
  return res.toLowerCase();
};

const isClaimFillingDeadline = (param: string) => {
  if (typeof param !== 'string') {
    return false;
  }

  return param.toLowerCase().trim().startsWith(
    'Claim Filing Deadline'.toLowerCase().trim()
  );
}

const extractCaveatValue = (param: string) => {
  const dataParts = param.split(':');
  dataParts.shift();
  const data = dataParts.join(':');
  return data.trim();
}

const compareClaimFilingDeadline = (aParam: string, bParam: string) => {
  const aValue = extractCaveatValue(aParam);
  const bValue = extractCaveatValue(bParam);
  if (isStringifyTimePeriod(aValue.toLowerCase().trim()) && isStringifyTimePeriod(bValue.toLowerCase().trim())) {
    return compareTimePeriod(aValue.toLowerCase().trim(), bValue.toLowerCase().trim());
  }
  return aParam.toLowerCase().trim() === bParam.toLowerCase().trim();
}

const isStringifyTimePeriod = (param: string) => {
  if (typeof param !== 'string') {
    return false;
  }
  const testRegex = new RegExp(/(\d+\s{0,}(year(s){0,1}|day(s){0,1}|week(s){0,1}|month(s){0,1}))|none|0/, 'i');
  return testRegex.test(param);
}

const unifyTimePeriod = (param: string) => {
  const regex = new RegExp(/(\d+)\s{0,}([a-z]*)/, 'ig');
  
  if (param === 'none' || param === '0') {
    return {
      unified: true,
      result: {
        quantiry: 0,
        unit: 'days'
      }
    };
  }

  const [, quantity, unit] = regex.exec(param) || [];
  if (!quantity || !unit) {
    return {
      unified: false,
      result: param
    };
  }
  let quantityNumber = Number(quantity);
  let unitLower = unit.toLowerCase().trim();

  if (new RegExp('day(s){0,1}', 'i').test(unitLower) && quantityNumber === 180) {
    quantityNumber = 6;
    unitLower = 'months';
  } else if (new RegExp('day(s){0,1}', 'i').test(unitLower) && quantityNumber === 365) {
    quantityNumber = 12;
    unitLower = 'months';
  } else if (new RegExp('day(s){0,1}', 'i').test(unitLower) && quantityNumber === 360) {
    quantityNumber = 12;
    unitLower = 'months';
  } else if (new RegExp('year(s){0,1}', 'i').test(unitLower) && quantityNumber === 1) {
    quantityNumber = 12;
    unitLower = 'months';
  }

  return {
    unified: true,
    result: {
      quantity: quantityNumber,
      unit: unitLower
    }
  }
}

const normalizeString = (param: string) => {
  return param
    .toLowerCase()
    .trim()
    .replace(/\s{1,}/g, ' ')
}

const compareTimePeriod = (aParam: string, bParam: string) => {
  const unifiedAParam = unifyTimePeriod(aParam);
  const unifiedBParam = unifyTimePeriod(bParam);

  if (!unifiedAParam.unified || !unifiedBParam.unified) {
    return aParam.toLowerCase().trim() === bParam.toLowerCase().trim();
  }

  return compareObjectImpl(
    unifiedAParam.result as ITimePeriod, 
    unifiedBParam.result as ITimePeriod
  );
}

const isWaitingPeriod = (param: {[key: string]: any}) => {
  const keys = Object.keys(param);
  return keys.length === 1 &&
    keys[0] === 'months' ||
    keys[0] === 'days' ||
    keys[0] === 'weeks' ||
    keys[0] === 'years';
}

const unifyWaitingPeriod = (param: {[key: string]: any}) => {
  const keys = Object.keys(param);
  if (keys.length === 0) {
    return param;
  }
  const key = keys[0];
  const value = param[key];

  if (key === 'months' && value === 3) {
    return {days: 90};
  }

  if (key === 'months' && value === 1) {
    return {days: 31};
  }

  if (key === 'days' && value === 30) {
    return {days: 31};
  }

  if (key === 'years' && value === 1) {
    return {days: 365};
  }

  if (key === 'months' && value === 6) {
    return {days: 180};
  }

  if (key === 'months' && value === 12) {
    return {days: 365};
  }

  if (key === 'weeks' && value === 4) {
    return {days: 31};
  }

  if (value === 0) {
    return {days: 0};
  }

  return param;
}

const compareLimitation = (
  aParam: {[key: string]: any}, 
  bParam: {[key: string]: any},
) => {
  const objA = cloneDeep(aParam);
  const objB = cloneDeep(bParam);
  if (objA.timePeriod === 'yr' && objA.frequency > 99) {
    objA.frequency = 99;
  }
  if (objB.timePeriod === 'yr' && objB.frequency > 99) {
    objB.frequency = 99;
  }

  if (objA.timePeriod === 'yr' && objA.frequency === 2) {
    objA.frequency = 24;
    objA.timePeriod = 'mo';
  }

  if (objB.timePeriod === 'yr' && objB.frequency === 2) {
    objB.frequency = 24;
    objB.timePeriod = 'mo';
  }

  return compareObjectImpl(objA, objB);
}

const compareWaitingPeriod = (
  aParam: {[key: string]: any}, 
  bParam: {[key: string]: any},
) => {
  const unitValueA = unifyWaitingPeriod(aParam);
  const unitValueB = unifyWaitingPeriod(bParam);

  return compareObjectImpl(unitValueA, unitValueB);
}

const isLimitation = (param: {[key: string]: any}) => {
  const keys = Object.keys(param);
  return keys.includes('quantity') && 
    keys.includes('frequency') && 
    keys.includes('timePeriod');
}

export const compareArrays = (aParam: Array<any>, bParam: Array<any>) => {
  if (aParam.length !== bParam.length) {
    return false;
  }
  let bParamCopy = [...bParam];

  const res = aParam.reduce((acc: boolean, current: any, index: number) => {
    const indexB = bParamCopy.findIndex((el) => compare(current, el));
    if (indexB !== -1) {
      bParamCopy.splice(indexB, 1);
    }
    return acc && indexB !== -1;
  }, true);

  return res && bParamCopy.length === 0;
};

export const compare = (aParam: any, bParam: any): boolean => {
  const paramATypeComplex = getType(aParam);
  const paramBTypeComplex = getType(bParam);
  const paramATypeSimple = typeof aParam;
  const paramBTypeSimple = typeof bParam;

  if (
    paramATypeComplex === paramBTypeComplex &&
    paramATypeSimple === paramBTypeSimple
  ) {
    if (
      paramATypeSimple === 'object' &&
      paramATypeComplex !== 'null' &&
      paramATypeComplex !== 'array' &&
      paramATypeComplex !== 'date'
    ) {
      return compareObject(aParam, bParam);
    } else if (paramATypeComplex === 'array') {
      return compareArrays(aParam, bParam);
    } else if (paramATypeComplex === 'date') {
      return +aParam === +bParam;
    } else if (paramATypeComplex === 'string') {
      if (isClaimFillingDeadline(aParam) && isClaimFillingDeadline(bParam)) {
        return compareClaimFilingDeadline(
          normalizeString(aParam),
          normalizeString(bParam)
        );
      }
      return normalizeString(aParam) === normalizeString(bParam);
    } else {
      return aParam === bParam;
    }
  } else {
    if (
      [paramATypeComplex, paramBTypeComplex].some((el) => el === 'number') &&
      [paramATypeComplex, paramBTypeComplex].some((el) => el === 'string') &&
      [aParam, bParam].some(
        (el) => getType(el) === 'string' && /^[0-9].*/.test(el)
      )
    ) {
      return Number(aParam) === Number(bParam);
    }
    return aParam === bParam;
  }
};

const compareObjectImpl = (
  objA: { [key: string]: any },
  objB: { [key: string]: any }
) => {
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);
  if (!isEqual(keysA.sort(), keysB.sort())) {
    return false;
  }

  return keysA.reduce((acc, current) => {
    return acc && compare(objA[current], objB[current]);
  }, true);
}

const compareObject = (
  objA: { [key: string]: any },
  objB: { [key: string]: any }
) => {

  if (isWaitingPeriod(objA) && isWaitingPeriod(objB)) {
    return compareWaitingPeriod(objA, objB);
  }

  if (isLimitation(objA) && isLimitation(objB)) {
    return compareLimitation(objA, objB);
  }

  return compareObjectImpl(objA, objB);
};